import React, { useState, useEffect, useCallback } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiCoffee, FiGrid, FiList, FiMonitor, FiExternalLink, FiRefreshCw } from 'react-icons/fi';
import Modal from '../components/ui/Modal';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { featureFlagEnabled } from '../config/modulos';
import { puede } from '../utils/permisos';

const ESTADO_LABEL = { pendiente_pago: 'Pendiente de pago', pendiente_despachar: 'Pendiente de despachar', en_preparacion: 'En preparación', servido: 'Servido', pagado: 'Pagado', cancelado: 'Cancelado' };
const ESTADO_BADGE = { pendiente_pago: 'badge-warning', pendiente_despachar: 'badge-info', en_preparacion: 'badge-info', servido: 'badge-ok', pagado: 'badge', cancelado: 'badge-danger' };
const SIGUIENTE = { pendiente_despachar: 'en_preparacion', en_preparacion: 'servido' };

export default function Restaurante() {
  const { usuario, empresa } = useAuth();
  const qrActiva = usuario?.rol === 'superadmin' || featureFlagEnabled(empresa, 'qr_mesa');
  const [tab, setTab] = useState('monitor');
  const [pedidos, setPedidos] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [carta, setCarta] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [ultimoRefresh, setUltimoRefresh] = useState(new Date());
  const [tick, setTick] = useState(Date.now());
  const [modalMesa, setModalMesa] = useState(false);
  const [modalArt, setModalArt] = useState(false);
  const [editMesa, setEditMesa] = useState(null);
  const [editArt, setEditArt] = useState(null);
  const [formMesa, setFormMesa] = useState({ numero: '', nombre: '', activa: true });
  const [formArt, setFormArt] = useState({ nombre: '', descripcion: '', descripcionLarga: '', categoria: 'General', precio: 0, stock: 0, foto: '', subproductos: [], extras: [], alergenos: [], disponible: true });

  const puedeEditar = puede(usuario, 'restaurante', 'editar');
  const puedeCrear = puede(usuario, 'restaurante', 'crear');
  const puedeEliminar = puede(usuario, 'restaurante', 'eliminar');

  if (!qrActiva) {
    return (
      <div className="page">
        <h1 className="page-title">Restaurante</h1>
        <div className="alert-warning">La funcionalidad de QR de mesa no está habilitada para esta empresa.</div>
      </div>
    );
  }

  const cargarMonitor = useCallback(async () => {
    try {
      const { data } = await api.get('/restaurante/pedidos', { params: { activos: true } });
      setPedidos(data);
      setUltimoRefresh(new Date());
    } catch { /* noop */ }
  }, []);

  const cargarTodo = useCallback(async () => {
    setCargando(true);
    try {
      const [pe, me, ca] = await Promise.all([
        api.get('/restaurante/pedidos', { params: { activos: true } }),
        api.get('/restaurante/mesas'),
        api.get('/restaurante/carta'),
      ]);
      setPedidos(pe.data); setMesas(me.data); setCarta(ca.data);
    } catch { /* noop */ } finally { setCargando(false); }
  }, []);

  useEffect(() => { cargarTodo(); }, [cargarTodo]);

  // Refresco automático del monitor cada 15s
  useEffect(() => {
    if (tab !== 'monitor') return undefined;
    const id = setInterval(cargarMonitor, 15000);
    return () => clearInterval(id);
  }, [tab, cargarMonitor]);

  useEffect(() => {
    if (tab !== 'monitor') return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [tab]);

  const cambiarEstado = async (id, estado) => {
    try { await api.patch(`/restaurante/pedidos/${id}/estado`, { estado }); cargarMonitor(); }
    catch { alert('No se pudo actualizar'); }
  };

  // --- Mesas ---
  const guardarMesa = async (e) => {
    e.preventDefault();
    try {
      if (editMesa) await api.put(`/restaurante/mesas/${editMesa._id}`, formMesa);
      else await api.post('/restaurante/mesas', formMesa);
      setModalMesa(false); cargarTodo();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };
  const eliminarMesa = async (m) => {
    if (!window.confirm(`¿Eliminar la mesa ${m.numero}?`)) return;
    try { await api.delete(`/restaurante/mesas/${m._id}`); cargarTodo(); } catch { alert('Error'); }
  };
  const urlQR = (m) => `${window.location.origin}/carta/${m.qrToken}`;

  // --- Carta ---
  const guardarArt = async (e) => {
    e.preventDefault();
    try {
      const datos = { ...formArt, precio: Number(formArt.precio), stock: Number(formArt.stock) };
      if (editArt) await api.put(`/restaurante/carta/${editArt._id}`, datos);
      else await api.post('/restaurante/carta', datos);
      setModalArt(false); cargarTodo();
    } catch (err) { alert(err.response?.data?.error || 'Error'); }
  };

  const onFotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setFormArt((f) => ({ ...f, foto: reader.result }));
    };
    reader.readAsDataURL(file);
  };
  const eliminarArt = async (a) => {
    if (!window.confirm(`¿Eliminar "${a.nombre}"?`)) return;
    try { await api.delete(`/restaurante/carta/${a._id}`); cargarTodo(); } catch { alert('Error'); }
  };

  const formatearTiempo = (fecha) => {
    const inicio = new Date(fecha).getTime();
    const ahora = tick;
    const diff = Math.max(0, Math.floor((ahora - inicio) / 1000));
    const horas = Math.floor(diff / 3600);
    const minutos = Math.floor((diff % 3600) / 60);
    const segundos = diff % 60;
    if (horas > 0) return `${horas}h ${minutos}m`;
    if (minutos > 0) return `${minutos}m ${segundos}s`;
    return `${segundos}s`;
  };

  return (
    <div className="page">
      <div className="page-head">
        <div><h1 className="page-title">Restaurante</h1><p className="page-subtitle">Comandas por QR, pago y monitor en vivo</p></div>
        {tab === 'monitor' && (
          <div className="monitor-toolbar">
            <span className="monitor-refresh">Actualización automática cada 15s</span>
            <button className="btn" onClick={cargarMonitor}><FiRefreshCw /> Actualizar</button>
          </div>
        )}
        {tab === 'mesas' && puedeCrear && <button className="btn btn-primary" onClick={() => { setEditMesa(null); setFormMesa({ numero: '', nombre: '', activa: true }); setModalMesa(true); }}><FiPlus /> Nueva mesa</button>}
        {tab === 'carta' && puedeCrear && <button className="btn btn-primary" onClick={() => { setEditArt(null); setFormArt({ nombre: '', descripcion: '', descripcionLarga: '', categoria: 'General', precio: 0, stock: 0, foto: '', subproductos: [], extras: [], alergenos: [], disponible: true }); setModalArt(true); }}><FiPlus /> Nuevo artículo</button>}
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'monitor' ? 'activo' : ''}`} onClick={() => setTab('monitor')}><FiMonitor /> Monitor <span className="tab-count">{pedidos.length}</span></button>
        <button className={`tab ${tab === 'mesas' ? 'activo' : ''}`} onClick={() => setTab('mesas')}><FiGrid /> Mesas <span className="tab-count">{mesas.length}</span></button>
        <button className={`tab ${tab === 'carta' ? 'activo' : ''}`} onClick={() => setTab('carta')}><FiList /> Carta <span className="tab-count">{carta.length}</span></button>
      </div>

      {cargando ? <p className="empty">Cargando…</p> : (
        <>
          {tab === 'monitor' && (
            pedidos.length === 0 ? <p className="empty"><FiCoffee /> No hay comandas activas.</p> : (
              <div className="monitor-grid">
                {pedidos.map((p) => {
                  const tieneNotas = (p.lineas || []).some((l) => l.notas);
                  const tiempoSegundos = Math.max(0, Math.floor((tick - new Date(p.createdAt).getTime()) / 1000));
                  const alertaTiempo = tiempoSegundos >= 900;
                  return (
                  <div key={p._id} className={`comanda-card estado-${p.estado}${tieneNotas ? ' comanda-card-warning' : ''}${alertaTiempo ? ' comanda-card-alerta' : ''}`}>
                    <div className="comanda-head">
                      <strong>Mesa {p.mesaNumero}</strong>
                      <div className="comanda-head-badges">
                        {alertaTiempo && <span className="badge badge-danger">⏰ Demorado</span>}
                        {tieneNotas && <span className="badge badge-warning">Revisar nota</span>}
                        <span className={`badge ${ESTADO_BADGE[p.estado]}`}>{ESTADO_LABEL[p.estado]}</span>
                      </div>
                    </div>
                    <ul className="comanda-lineas">
                      {p.lineas.map((l, i) => (
                        <li key={i} className={l.notas ? 'comanda-linea-warning' : ''}>
                          <div>{l.cantidad}× {l.nombre}</div>
                          {l.extras?.length > 0 && (
                            <div className="comanda-linea-detalle">Extras: {l.extras.map((e) => e.nombre).join(', ')}</div>
                          )}
                          {l.notas && <div className="comanda-linea-nota">⚠️ {l.notas}</div>}
                        </li>
                      ))}
                    </ul>
                    <div className="comanda-foot">
                      <div className="comanda-meta">
                        <span className="comanda-tiempo">⏱️ {formatearTiempo(p.createdAt)}</span>
                        <span className="comanda-total">{(p.total || 0).toFixed(2)} €{p.pagado ? ' · Pagado' : ''}</span>
                      </div>
                      {puedeEditar && (
                        <div className="comanda-acciones">
                          {SIGUIENTE[p.estado] && <button className="btn btn-sm btn-primary" onClick={() => cambiarEstado(p._id, SIGUIENTE[p.estado])}>{ESTADO_LABEL[SIGUIENTE[p.estado]]}</button>}
                          <button className="btn btn-sm" onClick={() => cambiarEstado(p._id, 'cancelado')}>Cancelar</button>
                        </div>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            )
          )}

          {tab === 'mesas' && (
            mesas.length === 0 ? <p className="empty">No hay mesas.</p> : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>Número</th><th>Nombre</th><th>Estado</th><th>Enlace QR</th><th></th></tr></thead>
                  <tbody>
                    {mesas.map((m) => (
                      <tr key={m._id}>
                        <td data-label="Número"><strong>{m.numero}</strong></td>
                        <td data-label="Nombre">{m.nombre || '—'}</td>
                        <td data-label="Estado"><span className={`badge ${m.activa ? 'badge-ok' : 'badge-danger'}`}>{m.activa ? 'Activa' : 'Inactiva'}</span></td>
                        <td data-label="QR"><a href={urlQR(m)} target="_blank" rel="noreferrer" className="link"><FiExternalLink /> Abrir carta</a></td>
                        <td className="acciones">
                          {puedeEditar && <button className="icon-btn" onClick={() => { setEditMesa(m); setFormMesa({ numero: m.numero, nombre: m.nombre, activa: m.activa }); setModalMesa(true); }}><FiEdit2 /></button>}
                          {puedeEliminar && <button className="icon-btn danger" onClick={() => eliminarMesa(m)}><FiTrash2 /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}

          {tab === 'carta' && (
            carta.length === 0 ? <p className="empty">La carta está vacía.</p> : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead><tr><th>Artículo</th><th>Categoría</th><th>Precio</th><th>Disponible</th><th></th></tr></thead>
                  <tbody>
                    {carta.map((a) => (
                      <tr key={a._id}>
                        <td data-label="Artículo"><strong>{a.nombre}</strong>{a.descripcion && <span className="sub"> · {a.descripcion}</span>}</td>
                        <td data-label="Categoría">{a.categoria}</td>
                        <td data-label="Precio">{(a.precio || 0).toFixed(2)} €</td>
                        <td data-label="Disponible"><span className={`badge ${a.disponible ? 'badge-ok' : 'badge-danger'}`}>{a.disponible ? 'Sí' : 'No'}</span></td>
                        <td className="acciones">
                          {puedeEditar && <button className="icon-btn" onClick={() => { setEditArt(a); setFormArt({ nombre: a.nombre, descripcion: a.descripcion || '', descripcionLarga: a.descripcionLarga || '', categoria: a.categoria, precio: a.precio, stock: a.stock ?? 0, foto: a.foto || '', subproductos: a.subproductos || [], extras: a.extras || [], alergenos: a.alergenos || [], disponible: a.disponible }); setModalArt(true); }}><FiEdit2 /></button>}
                          {puedeEliminar && <button className="icon-btn danger" onClick={() => eliminarArt(a)}><FiTrash2 /></button>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )}
        </>
      )}

      <Modal abierto={modalMesa} onCerrar={() => setModalMesa(false)} titulo={editMesa ? 'Editar mesa' : 'Nueva mesa'} ancho={420}>
        <form onSubmit={guardarMesa} className="form-grid">
          <label className="field"><span>Número *</span><input required value={formMesa.numero} onChange={(e) => setFormMesa((f) => ({ ...f, numero: e.target.value }))} /></label>
          <label className="field"><span>Nombre</span><input value={formMesa.nombre} onChange={(e) => setFormMesa((f) => ({ ...f, nombre: e.target.value }))} /></label>
          <label className="field checkbox span-2"><input type="checkbox" checked={formMesa.activa} onChange={(e) => setFormMesa((f) => ({ ...f, activa: e.target.checked }))} /><span>Mesa activa</span></label>
          <div className="form-actions span-2"><button type="button" className="btn" onClick={() => setModalMesa(false)}>Cancelar</button><button type="submit" className="btn btn-primary">Guardar</button></div>
        </form>
      </Modal>

      <Modal abierto={modalArt} onCerrar={() => setModalArt(false)} titulo={editArt ? 'Editar artículo' : 'Nuevo artículo'} ancho={480}>
        <form onSubmit={guardarArt} className="form-grid">
          <label className="field span-2"><span>Nombre *</span><input required value={formArt.nombre} onChange={(e) => setFormArt((f) => ({ ...f, nombre: e.target.value }))} /></label>
          <label className="field"><span>Categoría</span><input value={formArt.categoria} onChange={(e) => setFormArt((f) => ({ ...f, categoria: e.target.value }))} /></label>
          <label className="field"><span>Precio (€)</span><input type="number" step="0.01" value={formArt.precio} onChange={(e) => setFormArt((f) => ({ ...f, precio: e.target.value }))} /></label>
          <label className="field"><span>Stock</span><input type="number" min="0" step="1" value={formArt.stock} onChange={(e) => setFormArt((f) => ({ ...f, stock: Number(e.target.value) }))} /></label>
          <label className="field span-2"><span>Descripción corta</span><input value={formArt.descripcion} onChange={(e) => setFormArt((f) => ({ ...f, descripcion: e.target.value }))} /></label>
          <label className="field span-2"><span>Descripción detallada</span><textarea rows={3} value={formArt.descripcionLarga} onChange={(e) => setFormArt((f) => ({ ...f, descripcionLarga: e.target.value }))} /></label>
          <label className="field span-2"><span>Foto del producto</span><input type="file" accept="image/*" onChange={onFotoChange} /></label>
          {formArt.foto && <label className="field span-2"><span>Vista previa</span><img src={formArt.foto} alt="preview" style={{ maxHeight: 140, objectFit: 'cover', borderRadius: 10, border: '1px solid #e2e8f0' }} /></label>}
          <label className="field span-2"><span>O usar URL</span><input value={formArt.foto} onChange={(e) => setFormArt((f) => ({ ...f, foto: e.target.value }))} /></label>
          <label className="field span-2"><span>Subproductos / extras sugeridos (uno por línea)</span><textarea rows={3} value={(formArt.subproductos || []).map((s) => s.nombre ? `${s.nombre}:${s.precio ?? 0}` : '').join('\n')} onChange={(e) => setFormArt((f) => ({ ...f, subproductos: e.target.value.split('\n').filter(Boolean).map((linea) => { const [nombre, precio] = linea.split(':'); return { nombre: nombre?.trim() || '', precio: Number(precio) || 0 }; }) }))} /></label>
          <label className="field span-2"><span>Extras seleccionables (nombre:precio)</span><textarea rows={3} value={(formArt.extras || []).map((e) => e.nombre ? `${e.nombre}:${e.precio ?? 0}` : '').join('\n')} onChange={(e) => setFormArt((f) => ({ ...f, extras: e.target.value.split('\n').filter(Boolean).map((linea) => { const [nombre, precio] = linea.split(':'); return { nombre: nombre?.trim() || '', precio: Number(precio) || 0, seleccionable: true }; }) }))} /></label>
          <label className="field span-2"><span>Alergenos (separados por comas)</span><input value={(formArt.alergenos || []).join(', ')} onChange={(e) => setFormArt((f) => ({ ...f, alergenos: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) }))} /></label>
          <label className="field checkbox span-2"><input type="checkbox" checked={formArt.disponible} onChange={(e) => setFormArt((f) => ({ ...f, disponible: e.target.checked }))} /><span>Disponible</span></label>
          <div className="form-actions span-2"><button type="button" className="btn" onClick={() => setModalArt(false)}>Cancelar</button><button type="submit" className="btn btn-primary">Guardar</button></div>
        </form>
      </Modal>
    </div>
  );
}
